import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, computed, inject } from '@angular/core';
import {
    IonBackButton,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonReorder,
    IonReorderGroup,
    IonTitle,
    IonToolbar,
    ModalController,
    PopoverController,
} from '@ionic/angular/standalone';
import { Subscription } from 'rxjs';
import { AddSignalComponent } from './add-signal.component';
import { EditSignalComponent } from './edit-signal.component';
import { SignalStylePopoverComponent } from './signal-style-popover.component';
import { arrayMax } from '../../hgm-setup/src/module/util/core';
import { DataProvider } from '../../providers/data-provider';
import { LayoutEntry, SignalGroupProvider } from '../../providers/signal-group-provider';
import { SignalSettingsProvider, maxSignalDashLength, signalColors, signalDashes } from '../../providers/signal-settings-provider';
import { LineSampleComponent } from '../line-sample.component';

@Component({
    selector: 'app-tab-signals',
    host: { class: 'ion-page' },
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        IonHeader,
        IonToolbar,
        IonTitle,
        IonContent,
        IonItem,
        IonLabel,
        IonIcon,
        IonButton,
        IonButtons,
        IonBackButton,
        IonReorder,
        IonReorderGroup,
        LineSampleComponent,
    ],
    template: `
        <ion-header>
            <ion-toolbar>
                <ion-buttons slot="start">
                    <ion-back-button defaultHref="/tabs"></ion-back-button>
                </ion-buttons>
                <ion-title>Signals</ion-title>
            </ion-toolbar>
            @if (isLoaded()) {
                <ion-toolbar>
                    <ion-item lines="none">
                        <ion-button slot="start" (click)="addSignal($event)" [disabled]="!hasSignals()">
                            <ion-icon slot="start" name="add"></ion-icon>
                            Signal
                        </ion-button>
                        <ion-button slot="start" (click)="addDivider()">
                            <ion-icon slot="start" name="add"></ion-icon>
                            Divider
                        </ion-button>
                        <ion-button slot="end" (click)="reset()">
                            <ion-icon slot="start" name="refresh"></ion-icon>
                            Defaults
                        </ion-button>
                    </ion-item>
                </ion-toolbar>
            }
        </ion-header>

        @if (dataError()) {
            <ion-item class="wrapped" lines="full">{{ dataError() }}</ion-item>
        }

        <ion-content>
            @if (!isLoaded()) {
                <ion-item lines="none">
                    <ion-label class="wrapped ion-text-center">Signals can be rearranged here once a log file is loaded.</ion-label>
                </ion-item>
            }
            <ion-reorder-group disabled="false" (ionItemReorder)="onReorder($event)">
                @for (entry of layout; track entry.key) {
                    @switch (entry.type) {
                        @case ('signal') {
                            <ion-item class="signal" mode="ios" button detail="false" (click)="editSignal(entry)">
                                <ion-reorder slot="start"></ion-reorder>
                                <ion-label>{{ entry.name }}</ion-label>
                                <div
                                    slot="end"
                                    style="display: flex; align-items: center; gap: 12px; margin-right: 8px"
                                    (click)="$event.stopPropagation()"
                                >
                                    <div
                                        (click)="openStyleSettings($event, entry)"
                                        style="
                                            border: 1px solid var(--ion-color-step-400);
                                            border-radius: 4px;
                                            padding: 4px 8px;
                                            display: flex;
                                            align-items: center;
                                            cursor: pointer;
                                            min-height: 28px;
                                        "
                                    >
                                        <line-sample
                                            [color]="getColor(entry.key)"
                                            [dash]="getDash(entry.key)"
                                            [width]="sampleWidth"
                                        ></line-sample>
                                    </div>
                                </div>
                                <ion-button slot="end" fill="clear" (click)="removeEntry(entry); $event.stopPropagation()">
                                    <ion-icon name="remove-circle" color="danger" slot="icon-only"></ion-icon>
                                </ion-button>
                            </ion-item>
                        }
                        @case ('divider') {
                            <ion-item mode="ios" button detail="false">
                                <ion-reorder slot="start"></ion-reorder>
                                <hr
                                    style="flex-grow: 1; border-color: var(--ion-color-step-600); border-width: 1px 0 0 0; margin: 0 10px"
                                />
                                <ion-button slot="end" fill="clear" (click)="removeEntry(entry)">
                                    <ion-icon name="remove-circle" color="danger" slot="icon-only"></ion-icon>
                                </ion-button>
                            </ion-item>
                        }
                    }
                }
            </ion-reorder-group>
        </ion-content>
    `,
    styles: `
        ion-label.wrapped {
            text-overflow: unset !important;
            white-space: normal !important;
            overflow: auto !important;
        }

        ion-item [slot='end'] {
            margin-left: 8px;
            margin-right: 8px;
        }

        ion-button {
            margin-inline-start: 6px;
            margin-inline-end: 6px;
        }

        ion-item.signal {
            cursor: pointer;
        }
    `,
})
export class TabSignalsPageComponent implements OnInit, OnDestroy {
    readonly dataError = computed(() => this.dataProvider.result()?.error);
    readonly isLoaded = computed(() => !!this.dataProvider.data());
    readonly hasSignals = computed(() => this.isLoaded() && Object.keys(this.dataProvider.data()!.signals).length > 0);

    private subscriptions: Subscription[] = [];

    private readonly dataProvider = inject(DataProvider);
    private readonly modalCtrlr = inject(ModalController);
    private readonly popoverCtrlr = inject(PopoverController);
    private readonly signalGroupProvider = inject(SignalGroupProvider);
    private readonly signalSettingsProvider = inject(SignalSettingsProvider);
    private readonly cdr = inject(ChangeDetectorRef);

    ngOnInit() {
        this.subscriptions.push(this.signalSettingsProvider.styledDataObs.subscribe(() => this.cdr.markForCheck()));
        this.subscriptions.push(this.signalSettingsProvider.rangesObs.subscribe(() => this.cdr.markForCheck()));
    }

    ngOnDestroy() {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }

    get layout() {
        return this.signalGroupProvider.layout;
    }

    async addSignal(event: MouseEvent | TouchEvent) {
        const popover = await this.popoverCtrlr.create({
            component: AddSignalComponent,
            event,
        });
        await popover.present();
    }

    async editSignal(signal: LayoutEntry) {
        const popover = await this.modalCtrlr.create({
            component: EditSignalComponent,
            componentProps: { signal },
            breakpoints: [0, 0.75, 1],
            initialBreakpoint: 0.75,
        });
        await popover.present();
    }

    async addDivider() {
        const existingKeyIndices = this.signalGroupProvider.layout
            .filter((i) => i.type === 'divider')
            .map((i) => i.key)
            .map((key) => parseInt(key.replace('divider', ''), 10));
        const newIndex = arrayMax(existingKeyIndices) + 1;
        const newLayout = this.signalGroupProvider.layout.slice();
        newLayout.unshift({ type: 'divider', key: 'divider' + newIndex });
        this.signalGroupProvider.layout = newLayout;
    }

    removeEntry(entry: LayoutEntry) {
        this.signalGroupProvider.layout = this.signalGroupProvider.layout.filter((e) => e !== entry);
    }

    reset() {
        this.signalSettingsProvider.reset();
        this.signalGroupProvider.reset();
    }

    onReorder(event: any) {
        const newEntries = this.signalGroupProvider.layout.slice();
        newEntries.splice(event.detail.to, 0, ...newEntries.splice(event.detail.from, 1));
        this.signalGroupProvider.layout = newEntries;
        event.detail.complete();
    }

    getStyle(key: string) {
        return this.signalSettingsProvider.computedStyles[key] || { color: 0, dash: 0 };
    }

    getColor(key: string) {
        return signalColors[this.getStyle(key).color] || signalColors[0];
    }

    getDash(key: string) {
        return signalDashes[this.getStyle(key).dash] || signalDashes[0];
    }

    get sampleWidth() {
        return maxSignalDashLength;
    }

    async openStyleSettings(event: MouseEvent | TouchEvent, signal: LayoutEntry) {
        event.stopPropagation();
        const popover = await this.popoverCtrlr.create({
            component: SignalStylePopoverComponent,
            componentProps: { signalKey: signal.key },
            event,
            translucent: true,
            mode: 'ios',
        });
        await popover.present();
    }
}
